<script setup>
import {
  PlusIcon,
  RectangleStackIcon,
} from "@heroicons/vue/24/outline"
import { useForm } from "@inertiajs/vue3"
import { computed, onMounted, ref, watch } from "vue"
import { PrimaryButton, Modal, Select } from "@codinglabsau/ui"
import { NodeViewWrapper, nodeViewProps, NodeViewContent } from "@tiptap/vue-3"
import useNodeProps from "@/Composables/useNodeProps"
import EditableNodeWrapper from "../../Components/EditableNodeWrapper.vue"
import Grid from "@/Components/Inputs/BlockEditor/Extension/Components/Grid.vue"
import EditGrid from "@/Components/Inputs/BlockEditor/Extension/Modals/EditGrid.vue"
import LiveEventCardPreview from "../../Components/LiveEvents/LiveEventCardPreview.vue"

const props = defineProps({
  ...nodeViewProps,
})

let {
  cols,
  spacing,
} = useNodeProps(props)
const showModal = ref(false)

let recentLiveEvents = ref([])
let selectedLiveEvents = ref([])

onMounted(() => {
  recentLiveEvents.value = [...props.extension.options.upcomingLiveEvents];

  if (props.extension.options.data.hasOwnProperty(props.node.attrs.id)) {
    selectedLiveEvents.value = [...props.extension.options.data[props.node.attrs.id].events]
  }
})

const computedOptions = computed(() => {
  return recentLiveEvents.value.map((liveEvent) => {
    return {
      id: liveEvent.id,
      name: liveEvent.store.name  + ' - ' + moment(liveEvent.starts_at).format('lll') + ' - ' + liveEvent.title,
    }
  })
})

const attachLiveEventForm = useForm({
  liveEvent: null,
})

const addLiveEvent = (liveEvent) => {
  if (selectedLiveEvents.value.find(obj => obj.id === liveEvent)) {
    alert(__('Live event already added'))
    return
  }

  const eventToAdd = recentLiveEvents.value.find(obj => obj.id === liveEvent);

  if (eventToAdd) {
    selectedLiveEvents.value.push(eventToAdd)
    attachLiveEventForm.liveEvent = null
  }
}

const removeLiveEvent = (id) => {
  selectedLiveEvents.value = selectedLiveEvents.value.filter(liveEvent => liveEvent.id !== id);
}

watch(() => attachLiveEventForm.liveEvent, (liveEvent) => {
  if (liveEvent) {
    addLiveEvent(liveEvent);
  }
})

watch(() => selectedLiveEvents.value, () => {
  props.updateAttributes({
    events: selectedLiveEvents.value.map(liveEvent => liveEvent.id),
  })
}, {deep:true})
</script>

<template>
  <node-view-wrapper>
    <EditableNodeWrapper v-bind="props">
      <template #tools>
        <EditGrid
          v-model:cols="cols"
          v-model:spacing="spacing"
        />
      </template>
      <template #content>
        <div class="flex w-full">
          <node-view-content class="first:prose-headings:mt-2"/>
        </div>
        <div v-if="selectedLiveEvents.length > 0">
          <Grid
            :cols
            :spacing
          >
            <LiveEventCardPreview
              v-for="liveEvent in selectedLiveEvents"
              :live-event="liveEvent"
              :cols
              @remove="removeLiveEvent(liveEvent.id)"
            />
            <div
              @click.prevent="showModal = true"
              class="flex cursor-pointer relative p-6 w-full rounded-xl border-2 border-gray-300 border-dashed text-gray-400 hover:bg-gray-100">
              <div class="self-stretch">
                <div class="aspect-1">
                  <div class="absolute inset-0 flex items-center justify-center">
                    <div class="text-center">
                      {{ __('Add another Live Event') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Grid>
        </div>
        <div v-else class="my-4 text-center space-y-1">
          <RectangleStackIcon class="mx-auto w-12 h-12 text-gray-300"/>
          <h3 class="text-sm font-semibold text-gray-600">
            {{ __('No live events selected') }}
          </h3>
          <p class="text-gray-400 text-sm">
            {{ __('Select upcoming Live event') }}
          </p>
          <PrimaryButton
            @click="showModal = true"
            as="button"
            type="button"
          >
            <PlusIcon class="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true"/>
            {{ __('Select live events') }}
          </PrimaryButton>
        </div>
      </template>
    </EditableNodeWrapper>
  </node-view-wrapper>
  <Modal :open="showModal" @close="showModal = false">
    <template #header>
      <div>
        <h3 class="text-lg font-semibold text-gray-900">
          {{ __('Select live events') }}
        </h3>
      </div>
    </template>
    <Select
      v-model="attachLiveEventForm.liveEvent"
      :options="computedOptions"
    />
  </Modal>
</template>
